import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import {
  GraduationCap, ChevronRight, ChevronLeft, X, Check, Play, Pause,
  LayoutDashboard, Users, Target, Megaphone, Headphones, Wallet,
  Database, Shield, Settings, HelpCircle, Lightbulb, Zap, AlertTriangle, Move
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Tutorial Context
const TutorialContext = createContext();

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

// Tutorial steps configuration - Comprehensive interactive walkthroughs
const TUTORIAL_MODULES = [
  {
    id: 'getting_started',
    name: 'Getting Started',
    icon: GraduationCap,
    description: 'Learn the basics of UnifiCRM and navigate the platform',
    estimatedTime: '5 min',
    steps: [
      {
        id: 'welcome',
        title: 'Welcome to UnifiCRM! 🎉',
        content: 'UnifiCRM is your all-in-one platform for managing customer relationships, sales, marketing, support, and operations. This tutorial will guide you through the key features.',
        tip: 'You can access this tutorial anytime from the Help menu in the top right corner.',
        target: null,
        position: 'center'
      },
      {
        id: 'navigation',
        title: 'Navigation Sidebar',
        content: 'The sidebar on the left is your main navigation hub. Click on any module to expand its submenu and access different features.',
        tip: 'Use keyboard shortcut Ctrl/Cmd + K to quickly search and navigate to any page.',
        target: '[data-testid="sidebar"]',
        position: 'right',
        action: 'Click on "CRM" to expand the menu'
      },
      {
        id: 'dashboard',
        title: 'Your Dashboard',
        content: 'The dashboard shows key metrics, recent activity, and quick actions. Customize it by clicking "Customize Dashboard" to add or remove widgets.',
        tip: 'Board and Management dashboards are also available for executive views.',
        target: '[data-testid="main-content"]',
        position: 'left',
        link: '/dashboard'
      },
      {
        id: 'header_tools',
        title: 'Header Tools',
        content: 'The top bar contains notifications, theme toggle, and quick actions. Click the bell icon to see system notifications and the help icon to access tutorials.',
        tip: 'Purple badge shows pending customer portal submissions for admins.',
        target: null,
        position: 'center'
      },
      {
        id: 'settings',
        title: 'System Settings',
        content: 'Access Settings through the Tools menu to configure CRM settings, modules, VoIP, and more. Admins can enable/disable features per role.',
        tip: 'Module configuration lets you control which features are visible to each role.',
        target: '[data-testid="nav-tools"]',
        position: 'right',
        link: '/settings/crm'
      }
    ]
  },
  {
    id: 'crm',
    name: 'CRM & Contacts',
    icon: Database,
    description: 'Managing contacts, companies, and customer relationships',
    estimatedTime: '8 min',
    steps: [
      {
        id: 'contacts_overview',
        title: 'Contacts Management',
        content: 'The Contacts section is your central hub for all customer information. View, search, filter, and manage contacts with ease.',
        tip: 'Use the AI Health Score to identify contacts that need attention.',
        target: '[data-testid="nav-crm"]',
        position: 'right',
        link: '/contacts'
      },
      {
        id: 'add_contact',
        title: 'Adding New Contacts',
        content: 'Click "Add Contact" to create a new contact. Required fields: Name and Email. Optional: Phone, Company, Tags, Product Lines, and Segments.',
        example: 'Example: John Smith, john@company.com, (555) 123-4567, Acme Corp',
        tip: 'Click the green phone icon next to any phone number to initiate a call (VoIP required).',
        target: '[data-testid="add-contact-btn"]',
        position: 'bottom'
      },
      {
        id: 'contact_profile',
        title: 'Contact Profiles',
        content: 'Click on any contact to view their full profile including: Activity timeline, Linked deals and tickets, Communication history, and AI-generated insights.',
        tip: 'Portal Access badge shows if the customer can log into the customer portal.',
        target: null,
        position: 'center',
        link: '/contacts'
      },
      {
        id: 'import_export',
        title: 'Import & Export Contacts',
        content: 'Bulk import contacts from CSV/Excel files or export your contacts for backup. Click "Import/Export" on the contacts page.',
        example: 'Download our template to see the correct format for importing.',
        tip: 'Review imported contacts before confirming to catch any errors.',
        target: '[data-testid="import-export-btn"]',
        position: 'bottom',
        link: '/contacts/import-export'
      },
      {
        id: 'companies',
        title: 'Companies & Organizations',
        content: 'Link contacts to companies for B2B management. Track company-level metrics, deals, and relationships.',
        tip: 'Company profiles show all linked contacts and their combined deal value.',
        target: null,
        position: 'center',
        link: '/companies'
      },
      {
        id: 'segments',
        title: 'Customer Segments',
        content: 'Segment customers by type: Home, Small Business, Commercial, Scientific, Pharmaceutical, Industrial. Use segments for targeted marketing.',
        tip: 'Create custom segments based on behavior, purchase history, or demographics.',
        target: null,
        position: 'center',
        link: '/contacts'
      }
    ]
  },
  {
    id: 'sales',
    name: 'Sales & Deals',
    icon: Target,
    description: 'Managing your sales pipeline and closing deals',
    estimatedTime: '10 min',
    steps: [
      {
        id: 'deals_overview',
        title: 'Sales Pipeline',
        content: 'The Deals section shows your sales pipeline. Track opportunities from lead to close with customizable stages.',
        tip: 'Use the Kanban view for drag-and-drop pipeline management.',
        target: '[data-testid="nav-sales"]',
        position: 'right',
        link: '/deals'
      },
      {
        id: 'create_deal',
        title: 'Creating Deals',
        content: 'Click "New Deal" to create an opportunity. Set the deal name, value, expected close date, and link to a contact/company.',
        example: 'Example: "Enterprise License - Acme Corp" | Value: $50,000 | Close: March 2026',
        tip: 'Probability auto-adjusts based on pipeline stage but can be manually overridden.',
        target: '[data-testid="new-deal-btn"]',
        position: 'bottom'
      },
      {
        id: 'pipeline_stages',
        title: 'Pipeline Stages',
        content: 'Default stages: Qualification → Proposal → Negotiation → Closed Won/Lost. Drag deals between stages to update status.',
        tip: 'Customize stages in Settings → CRM Settings to match your sales process.',
        target: null,
        position: 'center'
      },
      {
        id: 'deal_activities',
        title: 'Deal Activities & Tasks',
        content: 'Add activities to deals: Calls, Meetings, Emails, Notes. Create follow-up tasks with due dates and reminders.',
        example: 'Schedule a demo call, send proposal, follow up on decision',
        tip: 'Activities are tracked in the timeline for complete deal history.',
        target: null,
        position: 'center'
      },
      {
        id: 'forecasting',
        title: 'Sales Forecasting',
        content: 'View sales forecasts based on weighted pipeline value. Filter by time period, rep, or product line.',
        tip: 'Forecast = Deal Value × Probability. Keep probabilities updated for accurate forecasting.',
        target: null,
        position: 'center',
        link: '/sales'
      },
      {
        id: 'leads',
        title: 'Lead Management',
        content: 'Leads are potential customers before they become contacts/deals. Score, qualify, and convert leads through your pipeline.',
        tip: 'Use Lead Scoring to prioritize high-value prospects automatically.',
        target: null,
        position: 'center',
        link: '/leads'
      }
    ]
  },
  {
    id: 'marketing',
    name: 'Marketing Hub',
    icon: Megaphone,
    description: 'Creating campaigns, automations, and creative content',
    estimatedTime: '12 min',
    steps: [
      {
        id: 'campaigns_overview',
        title: 'Marketing Campaigns',
        content: 'Create and manage multi-channel marketing campaigns. Track performance, engagement, and ROI all in one place.',
        tip: 'Use campaign templates to quickly launch proven campaign types.',
        target: '[data-testid="nav-marketing"]',
        position: 'right',
        link: '/campaigns'
      },
      {
        id: 'campaign_types',
        title: 'Campaign Types',
        content: 'Choose from: Email Campaigns, Social Media, Content Marketing, Paid Advertising, Events, and Multi-channel campaigns.',
        example: 'Product launch = Email + Social + Paid Ads working together',
        tip: 'Multi-channel campaigns coordinate messages across all platforms.',
        target: null,
        position: 'center'
      },
      {
        id: 'email_sequences',
        title: 'Email Sequences',
        content: 'Create automated email sequences for nurturing leads. Set triggers, delays, and personalization tokens.',
        example: 'Welcome series: Day 1 → Welcome | Day 3 → Features | Day 7 → Demo offer',
        tip: 'Use merge tags like {{first_name}} for personalization.',
        target: null,
        position: 'center',
        link: '/sales/email-sequences'
      },
      {
        id: 'automation_rules',
        title: 'Marketing Automation',
        content: 'Set up rules to automate marketing actions: Send emails when contacts visit pages, trigger campaigns based on behavior.',
        example: 'Trigger: Downloaded whitepaper → Action: Add to nurture sequence',
        tip: 'Combine multiple conditions for precise targeting.',
        target: null,
        position: 'center',
        link: '/marketing/automation'
      },
      {
        id: 'creative_hub',
        title: 'Creative Hub',
        content: 'Design marketing assets using the Creative Hub: Email templates, Landing pages, Social graphics, and Brand guidelines.',
        tip: 'Use AI content generation to create draft copy quickly.',
        target: null,
        position: 'center',
        link: '/marketing'
      },
      {
        id: 'content_calendar',
        title: 'Content Calendar',
        content: 'Plan and schedule content across all channels. View by day, week, or month. Drag to reschedule.',
        tip: 'Color-code by channel or campaign for easy visual planning.',
        target: null,
        position: 'center',
        link: '/marketing'
      },
      {
        id: 'marketing_analytics',
        title: 'Marketing Analytics',
        content: 'Track campaign performance: Open rates, Click rates, Conversions, ROI. Compare campaigns and identify winners.',
        tip: 'A/B test subject lines and content to improve performance.',
        target: null,
        position: 'center',
        link: '/marketing'
      }
    ]
  },
  {
    id: 'support',
    name: 'Service & Support',
    icon: Headphones,
    description: 'Managing tickets, live chat, and customer service',
    estimatedTime: '8 min',
    steps: [
      {
        id: 'tickets_overview',
        title: 'Support Tickets',
        content: 'The Tickets section shows all customer support requests. View, assign, prioritize, and resolve issues efficiently.',
        tip: 'Use filters to focus on high-priority or overdue tickets.',
        target: '[data-testid="nav-service"]',
        position: 'right',
        link: '/tickets'
      },
      {
        id: 'create_ticket',
        title: 'Creating Tickets',
        content: 'Tickets can be created manually, from emails, live chat, or customer portal. Set priority, category, and assignee.',
        example: 'Subject: "Product not working" | Priority: High | Category: Technical',
        tip: 'Link tickets to contacts for complete customer context.',
        target: null,
        position: 'center'
      },
      {
        id: 'ticket_workflow',
        title: 'Ticket Workflow',
        content: 'Default stages: Open → In Progress → Waiting on Customer → Resolved → Closed. SLA timers track response and resolution times.',
        tip: 'Automate ticket assignment based on category or workload.',
        target: null,
        position: 'center'
      },
      {
        id: 'live_chat',
        title: 'Live Chat',
        content: 'Enable real-time customer support with Live Chat. The floating chat widget appears when agents are available.',
        tip: 'Chat transcripts are saved and can be converted to tickets.',
        target: null,
        position: 'center',
        link: '/live-chat/dashboard'
      },
      {
        id: 'knowledge_base',
        title: 'Knowledge Base',
        content: 'Create self-service articles for common questions. Customers can find answers without contacting support.',
        tip: 'Track which articles are most viewed to identify common issues.',
        target: null,
        position: 'center'
      },
      {
        id: 'customer_portal',
        title: 'Customer Portal',
        content: 'Customers access the portal at /my-portal to upload files, submit testimonials, and chat with support.',
        tip: 'New portal submissions appear in the purple notification badge.',
        target: null,
        position: 'center',
        link: '/customer-portal-admin'
      }
    ]
  },
  {
    id: 'operations',
    name: 'Operations Hub',
    icon: Shield,
    description: 'Task management, workflows, and team operations',
    estimatedTime: '10 min',
    steps: [
      {
        id: 'operations_overview',
        title: 'Operations Hub',
        content: 'The Operations Hub centralizes task management, data governance, security settings, and audit logs.',
        tip: 'Use the Operations Dashboard for a quick overview of all activities.',
        target: '[data-testid="nav-operations"]',
        position: 'right',
        link: '/operations'
      },
      {
        id: 'task_management',
        title: 'Task Management',
        content: 'Create tasks for yourself or assign to team members. Set due dates, priorities, and link to deals/tickets/contacts.',
        example: 'Task: "Follow up with Acme Corp" | Due: Tomorrow | Priority: High | Linked: Deal #123',
        tip: 'Use task templates for recurring task types.',
        target: null,
        position: 'center'
      },
      {
        id: 'task_assignment',
        title: 'Task Assignment & Delegation',
        content: 'Assign tasks by clicking the assignee dropdown. Tasks appear in the assignee\'s personal dashboard. Track team workload.',
        tip: 'Round-robin assignment distributes tasks evenly across team.',
        target: null,
        position: 'center'
      },
      {
        id: 'workflows',
        title: 'Workflow Automation',
        content: 'Create automated workflows to handle repetitive processes. Triggers → Conditions → Actions.',
        example: 'When deal closes → Create onboarding task → Send welcome email → Update contact status',
        tip: 'Start with workflow templates and customize to your needs.',
        target: null,
        position: 'center',
        link: '/workflow-management'
      },
      {
        id: 'automation_rules',
        title: 'Automation Rules',
        content: 'Set up rules that run automatically: When a lead scores above 80 → Assign to sales | When ticket is overdue → Escalate.',
        example: 'Rule: Lead score > 80 → Auto-assign to senior sales rep',
        tip: 'Test rules with a few records before applying to all.',
        target: null,
        position: 'center'
      },
      {
        id: 'data_partitioning',
        title: 'Data Partitioning',
        content: 'Control data access by team, region, or product line. Users only see data relevant to their role.',
        tip: 'Useful for multi-region or multi-product organizations.',
        target: null,
        position: 'center'
      },
      {
        id: 'audit_logs',
        title: 'Audit Logs',
        content: 'Track all system changes: Who did what, when, and what changed. Essential for compliance and troubleshooting.',
        tip: 'Export audit logs for compliance reporting.',
        target: null,
        position: 'center',
        link: '/security'
      }
    ]
  },
  {
    id: 'reporting',
    name: 'Reports & Analytics',
    icon: Zap,
    description: 'Creating reports, dashboards, and data insights',
    estimatedTime: '8 min',
    steps: [
      {
        id: 'reports_overview',
        title: 'Reporting Overview',
        content: 'Generate reports across all modules: Sales, Marketing, Support, and Operations. Use pre-built or custom reports.',
        tip: 'Schedule reports to be emailed automatically.',
        target: null,
        position: 'center',
        link: '/reports'
      },
      {
        id: 'dashboard_builder',
        title: 'Custom Dashboard Builder',
        content: 'Create personalized dashboards with widgets for the metrics you care about. Drag and drop to arrange.',
        example: 'Widgets: Pipeline value, Open tickets, Campaign performance, Task completion',
        tip: 'Create role-specific dashboards: Executive, Sales, Support.',
        target: null,
        position: 'center',
        link: '/dashboard-builder'
      },
      {
        id: 'sales_reports',
        title: 'Sales Reports',
        content: 'Track sales performance: Revenue by rep, Pipeline velocity, Win/loss analysis, Forecast accuracy.',
        tip: 'Compare periods to identify trends and seasonality.',
        target: null,
        position: 'center'
      },
      {
        id: 'marketing_reports',
        title: 'Marketing Reports',
        content: 'Measure campaign effectiveness: ROI by channel, Lead source analysis, Content performance, Email metrics.',
        tip: 'Attribution reports show which touchpoints drive conversions.',
        target: null,
        position: 'center',
        link: '/reports'
      },
      {
        id: 'support_reports',
        title: 'Support Reports',
        content: 'Monitor support quality: Response times, Resolution rates, Customer satisfaction, Agent performance.',
        tip: 'Track SLA compliance to maintain service quality.',
        target: null,
        position: 'center'
      },
      {
        id: 'custom_reports',
        title: 'Custom Report Builder',
        content: 'Build reports with any data: Select fields, add filters, group by dimensions, choose visualizations.',
        example: 'Report: Deals by product line, grouped by quarter, filtered by region',
        tip: 'Save reports as templates for quick future access.',
        target: null,
        position: 'center'
      }
    ]
  },
  {
    id: 'finance',
    name: 'Finance & Billing',
    icon: Wallet,
    description: 'Managing invoices, payments, and financial operations',
    estimatedTime: '6 min',
    steps: [
      {
        id: 'finance_overview',
        title: 'Finance Hub',
        content: 'Track invoices, payments, and revenue. Integrate with accounting systems for seamless financial management.',
        tip: 'Auto-generate invoices when deals close.',
        target: '[data-testid="nav-finance"]',
        position: 'right',
        link: '/finance'
      },
      {
        id: 'invoices',
        title: 'Invoice Management',
        content: 'Create, send, and track invoices. Set payment terms, apply taxes, and accept online payments.',
        example: 'Invoice #1001 | Amount: $5,000 | Due: Net 30 | Status: Pending',
        tip: 'Enable automatic payment reminders for overdue invoices.',
        target: null,
        position: 'center'
      },
      {
        id: 'payments',
        title: 'Payment Processing',
        content: 'Accept payments via Stripe, PayPal, or bank transfer. Payments auto-reconcile with invoices.',
        tip: 'Set up recurring billing for subscription customers.',
        target: null,
        position: 'center'
      },
      {
        id: 'budgets',
        title: 'Budget Management',
        content: 'Set budgets by department, campaign, or project. Track spending against budget with alerts.',
        tip: 'Manager approval workflow available for budget requests.',
        target: null,
        position: 'center',
        link: '/finance'
      }
    ]
  },
  {
    id: 'ai_workforce',
    name: 'AI Virtual Team',
    icon: Zap,
    description: 'Learn how to use your AI team members throughout the CRM',
    estimatedTime: '8 min',
    steps: [
      {
        id: 'ai_intro',
        title: 'Meet Your AI Team 🤖',
        content: 'UnifiCRM includes AI-powered virtual team members that can help with research, content creation, data analysis, and more. They work alongside your human team using a multi-brain architecture.',
        tip: 'AI agents use OpenAI for analysis, Claude for creativity, and Gemini for synthesis.',
        target: null,
        position: 'center'
      },
      {
        id: 'ai_workforce_hub',
        title: 'AI Workforce Hub',
        content: 'Access the AI Workforce through HR Hub → AI Workforce. This is your command center for managing AI agents, viewing their outputs, and assigning tasks.',
        tip: 'The Dashboard tab shows real-time status of all AI agents and pending tasks.',
        target: '[data-testid="nav-hr-hub"]',
        position: 'right',
        link: '/hr-hub/ai-workforce'
      },
      {
        id: 'ai_agents',
        title: 'Available AI Agents',
        content: 'Your AI team includes specialists: Content Creators, Data Analysts, Research Assistants, and Leader/Manager agents. Each has unique capabilities and can be assigned to different task types.',
        example: 'Agent Types: Text, Image, Video, Voice, Data, Leader',
        tip: 'Create custom agents in the Agents tab with specific capabilities.',
        target: null,
        position: 'center',
        link: '/ai-workforce'
      },
      {
        id: 'ai_demo_mode',
        title: 'Demo Mode - AI Execution',
        content: 'Use Demo Mode to run AI tasks. Enter a high-level prompt like "Research top 10 SaaS companies in healthcare" and the AI team will break it down into subtasks using Left Brain (analysis), Right Brain (creativity), and Cortex (synthesis).',
        tip: 'View the detailed AI reasoning by clicking on any completed task.',
        target: '[data-testid="tab-demo"]',
        position: 'center',
        link: '/ai-workforce'
      },
      {
        id: 'ai_outputs',
        title: 'Viewing AI Outputs',
        content: 'AI outputs appear in multiple places: 1) Gallery tab for all deliverables, 2) Projects tab for grouped work, 3) Individual task cards with full reasoning breakdown.',
        tip: 'Export deliverables as Excel or Word documents for easy sharing.',
        target: '[data-testid="tab-gallery"]',
        position: 'center',
        link: '/ai-workforce'
      },
      {
        id: 'ai_assignment',
        title: 'Assigning AI to CRM Tasks',
        content: 'When creating tasks in Deals, Tickets, or any CRM module, you can assign them to AI agents! Look for the "Assign To" dropdown and select an AI team member.',
        example: 'Create a follow-up task → Assign to "Research Assistant AI" → AI generates research report',
        tip: 'AI agents appear with a robot icon in assignment dropdowns.',
        target: null,
        position: 'center'
      },
      {
        id: 'ai_playbook',
        title: 'Playbook Notes with AI',
        content: 'In Contact profiles, use Playbook Notes to track sales conversations. Record your responses and click "Generate AI Insight" to get suggestions from your AI team.',
        tip: 'AI insights can suggest follow-up actions and create tasks automatically.',
        target: null,
        position: 'center',
        link: '/contacts'
      },
      {
        id: 'ai_overnight',
        title: 'Overnight Work Queue',
        content: 'Queue tasks for automatic overnight execution. Go to AI Workforce → Overnight Queue, add tasks, and they will run after 4:30 PM. Check the Morning Review logs next day.',
        tip: 'Create Custom Processes to queue multiple related tasks at once.',
        target: '[data-testid="tab-overnight"]',
        position: 'center',
        link: '/ai-workforce'
      },
      {
        id: 'ai_resources',
        title: 'Resource Library',
        content: 'Upload documents, guidelines, and knowledge to the Resource Library. AI agents reference these when working on tasks to provide company-specific outputs.',
        tip: 'Link resources to specific projects in the Asset Library.',
        target: '[data-testid="tab-resources"]',
        position: 'center',
        link: '/ai-workforce'
      }
    ]
  }
];

// Onboarding checklist items
const ONBOARDING_CHECKLIST = [
  { id: 'profile', label: 'Complete your profile', path: '/my-settings', icon: Users },
  { id: 'contact', label: 'Add your first contact', path: '/contacts', icon: Users },
  { id: 'deal', label: 'Create your first deal', path: '/deals', icon: Target },
  { id: 'task', label: 'Create a task', path: '/operations', icon: Check },
  { id: 'bug_tracker', label: 'Report a bug (if found)', path: null, icon: AlertTriangle },
  { id: 'tutorial', label: 'Complete the tutorial', path: null, icon: GraduationCap }
];

// Tutorial Tooltip Component - Enhanced with tips, examples, links, and DRAGGABLE
const TutorialTooltip = ({ step, onNext, onPrev, onClose, currentIndex, totalSteps, moduleName, onNavigate }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: null, y: null });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef(null);

  // Reset position when step changes
  useEffect(() => {
    setPosition({ x: null, y: null });
  }, [currentIndex]);

  const handleMouseDown = (e) => {
    // Only drag from header area
    if (e.target.closest('[data-drag-handle]')) {
      setIsDragging(true);
      const rect = tooltipRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging && tooltipRef.current) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Keep within viewport bounds
      const rect = tooltipRef.current.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width;
      const maxY = window.innerHeight - rect.height;
      
      setPosition({
        x: Math.min(Math.max(0, newX), maxX),
        y: Math.min(Math.max(0, newY), maxY)
      });
    }
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!step) return null;

  const handleLinkClick = () => {
    if (step.link && onNavigate) {
      // Navigate without closing the tutorial
      onNavigate(step.link);
    }
  };

  // Calculate style for positioning
  const getPositionStyle = () => {
    if (position.x !== null && position.y !== null) {
      return {
        position: 'fixed',
        left: position.x,
        top: position.y,
        transform: 'none',
        maxHeight: '80vh'
      };
    }
    // Default position
    if (step.position === 'center') {
      return {
        top: '25%',
        left: '50%',
        transform: 'translateX(-50%)',
        maxHeight: '80vh'
      };
    }
    return {
      top: '6rem',
      right: '2rem',
      left: 'auto',
      transform: 'none',
      maxHeight: '80vh'
    };
  };

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none">
      {/* Tooltip - positioned and draggable */}
      <div 
        ref={tooltipRef}
        className={`absolute bg-white rounded-2xl shadow-2xl max-w-lg pointer-events-auto overflow-hidden border-2 border-blue-200 ${isDragging ? 'cursor-grabbing' : ''}`}
        style={getPositionStyle()}
        onMouseDown={handleMouseDown}
      >
        {/* Header - DRAG HANDLE */}
        <div 
          data-drag-handle
          className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white cursor-grab active:cursor-grabbing select-none"
        >
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Badge className="bg-white/20 text-white border-0">
                {moduleName} • Step {currentIndex + 1} of {totalSteps}
              </Badge>
              <span className="text-xs text-white/60 flex items-center gap-1">
                <Move className="h-3 w-3" /> Drag to move
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              data-testid="tutorial-close-btn"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <h3 className="text-xl font-bold">{step.title}</h3>
        </div>
        
        {/* Content */}
        <ScrollArea className="max-h-[50vh]">
          <div className="p-6 space-y-4">
            <p className="text-slate-700 leading-relaxed">{step.content}</p>
            
            {/* Example Box */}
            {step.example && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Example</p>
                    <p className="text-sm text-blue-700 mt-1">{step.example}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Tip Box */}
            {step.tip && (
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <HelpCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Pro Tip</p>
                    <p className="text-sm text-amber-700 mt-1">{step.tip}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Action hint */}
            {step.action && (
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Play className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">Try it now</p>
                    <p className="text-sm text-emerald-700 mt-1">{step.action}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Link button - navigates without closing tutorial */}
            {step.link && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLinkClick}
                className="w-full"
                data-testid="tutorial-go-to-section-btn"
              >
                Go to this section
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </ScrollArea>
        
        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t flex items-center justify-between">
          <div className="flex items-center gap-1">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === currentIndex ? 'bg-blue-500' : i < currentIndex ? 'bg-blue-300' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
          
          <div className="flex items-center gap-2">
            {currentIndex > 0 && (
              <Button variant="outline" size="sm" onClick={onPrev} data-testid="tutorial-prev-btn">
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
            <Button size="sm" onClick={onNext} className="bg-blue-600 hover:bg-blue-700" data-testid="tutorial-next-btn">
              {currentIndex === totalSteps - 1 ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Complete
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Tutorial Provider Component
export const TutorialProvider = ({ children }) => {
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(false);
  const [currentModule, setCurrentModule] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedModules, setCompletedModules] = useState([]);
  const [checklistCompleted, setChecklistCompleted] = useState([]);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  // Check if user wants to see tutorial on each login
  // IMPORTANT: Only show for CRM users, NOT on customer portal routes
  useEffect(() => {
    const tutorialDisabled = localStorage.getItem('tutorial_disabled') === 'true';
    const welcomeSeen = localStorage.getItem('tutorial_welcome_seen') === 'true';
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    // Don't show CRM tutorial on customer portal routes or for customer role users
    const isPortalRoute = window.location.pathname.startsWith('/my-portal') || 
                          window.location.pathname.startsWith('/customer-login') ||
                          window.location.pathname.startsWith('/portal');
    const isCustomerUser = user.role === 'customer';
    
    if (user.id && !tutorialDisabled && !welcomeSeen && !isPortalRoute && !isCustomerUser) {
      // Show the welcome once; afterwards it won't auto-popup on every navigation
      const timer = setTimeout(() => setShowWelcome(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Load checklist progress
  useEffect(() => {
    const saved = localStorage.getItem('onboarding_checklist');
    if (saved) {
      setChecklistCompleted(JSON.parse(saved));
    }
  }, []);

  const startTutorial = (moduleId = null) => {
    const module = moduleId 
      ? TUTORIAL_MODULES.find(m => m.id === moduleId)
      : TUTORIAL_MODULES[0];
    
    if (module) {
      setCurrentModule(module);
      setCurrentStepIndex(0);
      setIsActive(true);
    }
  };

  const stopTutorial = () => {
    setIsActive(false);
    setCurrentModule(null);
    setCurrentStepIndex(0);
  };

  const nextStep = () => {
    if (!currentModule) return;

    if (currentStepIndex < currentModule.steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      // Module completed
      setCompletedModules(prev => [...prev, currentModule.id]);
      
      // Find next module
      const currentModuleIndex = TUTORIAL_MODULES.findIndex(m => m.id === currentModule.id);
      if (currentModuleIndex < TUTORIAL_MODULES.length - 1) {
        setCurrentModule(TUTORIAL_MODULES[currentModuleIndex + 1]);
        setCurrentStepIndex(0);
      } else {
        // All modules completed
        stopTutorial();
        markChecklistItem('tutorial');
        toast.success('Tutorial completed! 🎉');
      }
    }
  };

  const prevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  // Navigate to a section without closing the tutorial
  const handleTutorialNavigate = (path) => {
    if (path) {
      navigate(path);
    }
  };

  const markChecklistItem = (itemId) => {
    if (!checklistCompleted.includes(itemId)) {
      const updated = [...checklistCompleted, itemId];
      setChecklistCompleted(updated);
      localStorage.setItem('onboarding_checklist', JSON.stringify(updated));
    }
  };

  const dismissWelcome = () => {
    setShowWelcome(false);
    localStorage.setItem('tutorial_welcome_seen', 'true');
    // Only save permanent preference if checkbox is checked
    if (dontShowAgain) {
      localStorage.setItem('tutorial_disabled', 'true');
    }
    setDontShowAgain(false);
  };

  const startWelcomeTutorial = () => {
    setShowWelcome(false);
    localStorage.setItem('tutorial_welcome_seen', 'true');
    // Save preference if checkbox is checked
    if (dontShowAgain) {
      localStorage.setItem('tutorial_disabled', 'true');
    }
    setDontShowAgain(false);
    startTutorial();
  };

  const currentStep = currentModule?.steps[currentStepIndex];
  const totalSteps = currentModule?.steps.length || 0;
  const checklistProgress = Math.round((checklistCompleted.length / ONBOARDING_CHECKLIST.length) * 100);

  const value = {
    isActive,
    currentModule,
    currentStep,
    completedModules,
    startTutorial,
    stopTutorial,
    nextStep,
    prevStep,
    showChecklist,
    setShowChecklist,
    checklistCompleted,
    markChecklistItem,
    checklistProgress
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}

      {/* Welcome Dialog */}
      <Dialog open={showWelcome} onOpenChange={(open) => {
        if (!open) dismissWelcome();
        else setShowWelcome(open);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <GraduationCap className="h-6 w-6 text-blue-500" />
              Welcome to UnifiCRM!
            </DialogTitle>
            <DialogDescription>
              Let&apos;s get you started with a quick tour of the platform.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6">
            <div className="grid grid-cols-3 gap-4 mb-6">
              {[
                { icon: Database, label: 'Manage Contacts', color: 'bg-blue-100 text-blue-600' },
                { icon: Target, label: 'Track Deals', color: 'bg-green-100 text-green-600' },
                { icon: Megaphone, label: 'Run Campaigns', color: 'bg-purple-100 text-purple-600' }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className={`w-12 h-12 mx-auto rounded-lg ${item.color} flex items-center justify-center mb-2`}>
                    <item.icon className="h-6 w-6" />
                  </div>
                  <p className="text-sm font-medium">{item.label}</p>
                </div>
              ))}
            </div>
            
            <p className="text-sm text-muted-foreground text-center mb-4">
              Would you like to take a guided tour or explore on your own?
            </p>
            
            {/* Don't show again checkbox */}
            <div className="flex items-center justify-center gap-2 pt-2 border-t">
              <Checkbox 
                id="dont-show-again" 
                checked={dontShowAgain}
                onCheckedChange={setDontShowAgain}
                data-testid="tutorial-dont-show-checkbox"
              />
              <label 
                htmlFor="dont-show-again" 
                className="text-sm text-muted-foreground cursor-pointer"
              >
                Don&apos;t show this again
              </label>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={dismissWelcome} className="w-full sm:w-auto" data-testid="explore-own-btn">
              Explore on My Own
            </Button>
            <Button onClick={startWelcomeTutorial} className="w-full sm:w-auto" data-testid="start-tutorial-btn">
              <Play className="h-4 w-4 mr-2" />
              Start Tutorial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tutorial Tooltip */}
      {isActive && currentStep && (
        <TutorialTooltip
          step={currentStep}
          onNext={nextStep}
          onPrev={prevStep}
          onClose={stopTutorial}
          currentIndex={currentStepIndex}
          totalSteps={totalSteps}
          moduleName={currentModule?.name || 'Tutorial'}
          onNavigate={handleTutorialNavigate}
        />
      )}

      {/* Checklist Panel */}
      <Dialog open={showChecklist} onOpenChange={setShowChecklist}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              Getting Started Checklist
            </DialogTitle>
            <DialogDescription>
              Complete these steps to get the most out of UnifiCRM
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span>Progress</span>
                <span className="font-medium">{checklistProgress}%</span>
              </div>
              <Progress value={checklistProgress} className="h-2" />
            </div>
            
            <div className="space-y-3">
              {ONBOARDING_CHECKLIST.map((item) => {
                const isCompleted = checklistCompleted.includes(item.id);
                const Icon = item.icon;
                
                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      isCompleted ? 'bg-green-50 border-green-200' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isCompleted ? 'bg-green-500 text-white' : 'bg-slate-100'
                    }`}>
                      {isCompleted ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                    </div>
                    <span className={`flex-1 ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                      {item.label}
                    </span>
                    {!isCompleted && item.path && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowChecklist(false);
                          window.location.href = item.path;
                        }}
                      >
                        Go
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    )}
                    {!isCompleted && item.id === 'tutorial' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowChecklist(false);
                          startTutorial();
                        }}
                      >
                        Start
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChecklist(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TutorialContext.Provider>
  );
};

// Tutorial Button Component (for use in header or sidebar)
export const TutorialButton = () => {
  const { startTutorial, setShowChecklist, checklistProgress } = useTutorial();

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setShowChecklist(true)}
      className="relative"
      data-testid="tutorial-btn"
    >
      <GraduationCap className="h-5 w-5" />
      {checklistProgress < 100 && (
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full animate-pulse" />
      )}
    </Button>
  );
};

// Tutorial Module List Component (for help section)
export const TutorialModuleList = () => {
  const { startTutorial, completedModules } = useTutorial();

  return (
    <div className="space-y-3">
      {TUTORIAL_MODULES.map((module) => {
        const Icon = module.icon;
        const isCompleted = completedModules.includes(module.id);
        
        return (
          <div
            key={module.id}
            className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
              isCompleted ? 'border-green-200 bg-green-50/50' : 'hover:bg-slate-50 hover:border-blue-200'
            }`}
            onClick={() => startTutorial(module.id)}
            data-testid={`tutorial-module-${module.id}`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isCompleted ? 'bg-green-100 text-green-600' : 'bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-600'
            }`}>
              <Icon className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-semibold">{module.name}</p>
                {module.estimatedTime && (
                  <Badge variant="outline" className="text-xs font-normal">
                    {module.estimatedTime}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground truncate">{module.description}</p>
              <p className="text-xs text-slate-400 mt-1">{module.steps.length} steps</p>
            </div>
            {isCompleted ? (
              <Badge className="bg-green-500 hover:bg-green-600">
                <Check className="h-3 w-3 mr-1" />
                Done
              </Badge>
            ) : (
              <Button variant="outline" size="sm" className="flex-shrink-0">
                <Play className="h-4 w-4 mr-1" />
                Start
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TutorialProvider;
